import {
	Option,
	Text,
	Combobox,
	ComboboxOpenChangeData,
	ComboboxOpenEvents,
	mergeClasses,
} from "@fluentui/react-components";
import {
	WrapUpState,
	useWrapUpStore,
	WrapUpActions,
} from "@/common/stores/nonpersistingStores/wrapup";
import { useMemo, useState, useCallback, useEffect } from "react";
import Timer from "@/components/atoms/timer/Timer";
import { useWrapUpStyle } from "./style";
import { useIntl } from "react-intl";
import { SquareToggleButton } from "@/components/atoms/squareToggleButton";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import {
	CanGenerateConversationSummary,
	changePresence,
	changeWrapUpCode,
	checkCanGenerateSummary,
	stopMessagingWrapUp,
} from "@/repository/queryFns";
import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import {
	Activity,
	Availabilities,
} from "@/components/organisms/currentUserPresence/types";
import { useMessage } from "@/common/messages";
import type {
	ComboboxProps,
	OnOpenChangeData,
	OpenPopoverEvents,
} from "@fluentui/react-components";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { InteractionPopoverType, InteractionType } from "@/common/types";
import { captureError } from "@/common/helpers/sentryHelper";
import { NotesButton } from "@/components/atoms/notes";
import { VoiceCallSummaryButton } from "@/components/atoms/voiceCallSummary";
import { Flex } from "@/components/atoms/flex";
import {
	QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { useQuery } from "@tanstack/react-query";
import { useSummaryGenerationStore } from "@/common/stores/nonpersistingStores/summaryGeneration/summaryGenerationStore";

export const WrapUp = () => {
	const intl = useIntl();
	const wrapUpStyle = useWrapUpStyle();
	const wrapUp = useWrapUpStore((state: WrapUpState) => state.wrapUp);
	const wrapUpUpdate = useWrapUpStore(
		(actions: WrapUpActions) => actions.update
	);
	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);
	const { show: showMessage } = useMessage();
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const currentQueue = useMemo(
		() => queues.find((q) => q.id === currentInteraction?.queueId),
		[queues, currentInteraction?.queueId]
	);

	const setSummaryGenerating = useSummaryGenerationStore(
		(state) => state.setGenerating
	);

	// A text interaction's close-time summary is generated in the background; flag the session so the
	// wrap-up summary/notes surfaces show "generating" until the TextSummaryReady event clears it.
	// Only auto-insert queues generate at close - without it the summary is produced on demand when the
	// agent presses the generate button, so flagging "generating" here would just flash and clear.
	useEffect(() => {
		if (
			wrapUp?.mediaId &&
			!wrapUp.isFromCall &&
			(currentQueue?.enableTextSummary ?? false) &&
			(currentQueue?.autoInsertTextSummary ?? false)
		) {
			setSummaryGenerating(wrapUp.mediaId);
		}
	}, [
		wrapUp?.mediaId,
		wrapUp?.isFromCall,
		currentQueue?.enableTextSummary,
		currentQueue?.autoInsertTextSummary,
		setSummaryGenerating,
	]);

	const { data: canGenerateSummary } = useQuery<boolean>({
		queryKey: [CanGenerateConversationSummary],
		queryFn: (_: any) => checkCanGenerateSummary(),
		refetchOnMount: "always",
	});

	const options = useMemo(() => {
		return wrapUp?.isLevelOneHidden
			? wrapUp?.allWrapUpCodesFromCollections?.filter((x) => x.level !== 1)
			: wrapUp?.allWrapUpCodesFromCollections;
	}, [wrapUp]);

	const timer = useMemo(() => {
		if (
			wrapUp?.wrapUpStartDate &&
			wrapUp?.wrapUpTimeInterval &&
			wrapUp?.wrapupTimerVisibility
		) {
			const endDate = new Date(
				new Date(wrapUp?.wrapUpStartDate).getTime() +
					wrapUp?.wrapUpTimeInterval * 1000
			).toString();
			return (
				<Timer
					startDate={endDate}
					isEndDate={true}
					className={wrapUpStyle.timer}
				/>
			);
		} else {
			return <></>;
		}
	}, [wrapUp]);

	// Persist the queue's default wrap-up code into the cache AFTER render. This seeding used to
	// happen inline in the selectedCode memo — a store write during render, which React flags
	// ("Cannot update a component (GlobalDataProvider) while rendering WrapUp"). WrapUp mounts in
	// the same commit that unmounts the CRM case picker (both react to WrapUpStarted), and the
	// mid-render root-level store update could interrupt that commit, stranding the picker's DOM
	// in the workspace after the interaction closed.
	useEffect(() => {
		const currentWrapUpCodeId = appStoreCache[CacheKey.CurrentWrapUpCodeId];

		if (wrapUp && !currentWrapUpCodeId && wrapUp.defaultAssignedWrapUpCode) {
			appStoreActions.setCache(
				CacheKey.CurrentWrapUpCodeId,
				wrapUp.defaultAssignedWrapUpCode.id
			);
			appStoreActions.setCache(
				CacheKey.CurrentWrapUpCodeName,
				wrapUp.defaultAssignedWrapUpCode.name
			);
		}
		// Narrow deps: only the cache key this effect reads + the stable setCache action.
		// appStoreActions is a `state => state` selection (new reference on every cache write), so
		// depending on the whole object would re-run this seed on unrelated cache mutations; the
		// setCache function itself is a stable zustand action reference across writes.
	}, [wrapUp, appStoreCache[CacheKey.CurrentWrapUpCodeId], appStoreActions.setCache]);

	const selectedCode = useMemo(() => {
		const currentWrapUpCodeId = appStoreCache[CacheKey.CurrentWrapUpCodeId];

		// Until the effect above lands the default in the cache, surface it directly so the
		// combobox shows the default code on the very first wrap-up render (same value the
		// old inline write returned).
		if (wrapUp && !currentWrapUpCodeId && wrapUp.defaultAssignedWrapUpCode) {
			return {
				id: wrapUp.defaultAssignedWrapUpCode.id,
				name: wrapUp.defaultAssignedWrapUpCode.name,
			};
		}

		return {
			id: appStoreCache[CacheKey.CurrentWrapUpCodeId],
			name: appStoreCache[CacheKey.CurrentWrapUpCodeName],
		};
	}, [
		wrapUp,
		appStoreCache[CacheKey.CurrentWrapUpCodeId],
		appStoreCache[CacheKey.CurrentWrapUpCodeName],
	]);

	const [inputCodeValue, setInputCodeValue] = useState(selectedCode.name ?? "");

	const setMatchingOptionsByValue = (value: string) => {
		const matches = options?.filter((option) =>
			option?.name?.toLowerCase().startsWith(value?.toLowerCase())
		);

		setMatchingOptions(matches ?? []);
	};

	const onCodeSelect = useCallback(
		(_: any, data: any) => {
			appStoreActions.setCache(CacheKey.CurrentWrapUpCodeId, data.optionValue);
			appStoreActions.setCache(CacheKey.CurrentWrapUpCodeName, data.optionText);

			const wrapupCode = options?.find((mo) => mo.id === data.optionValue);

			const wrapUpModel = {
				id: Lockr.get(StorageKeys.UserId),
				wrapUpCodeId: data.optionValue,
				wrapUpCodeName: data.optionText,
				wrapUpCollectionName: wrapupCode?.collectionName,
				wrapUpParentCodeName: wrapupCode?.parentName,
			};
			setInputCodeValue(data.optionText);
			setMatchingOptionsByValue(data.optionText);

			changeWrapUpCode(wrapUpModel);
		},
		[appStoreActions, setInputCodeValue, setMatchingOptionsByValue]
	);

	const endWrapUp = () => {
		const endCallWrapUp =
			currentInteraction?.type === InteractionType.Call &&
			currentInteraction?.id === wrapUp?.mediaId;
		const callInProgress = endCallWrapUp
			? false
			: appStoreCache[CacheKey.CallInProgress];

		const presenceChangeModel = {
			sip: Lockr.get(StorageKeys.SIP),
			presence: Availabilities.Available,
			note: Activity.Available,
			id: Lockr.get(StorageKeys.UserId),
			changedBy: "",
			isWrapUpCloseAction: true,
		};

		if (wrapUp) {
			wrapUpUpdate(null);
			if (currentInteraction) {
				currentInteraction.wrapUpStopped = true;
			}

			appStoreActions.removeCaches([CacheKey.SessionVariableText]);
		}

		if (!callInProgress) {
			changePresence(presenceChangeModel).catch((err: any) => {
				captureError(err, "Error on changing presence");
				showMessage("workspace", {
					intent: "error",
					text: `${intl.formatMessage({ id: "Common.Error" })} ${err}`,
				});
			});
		} else {
			stopMessagingWrapUp().catch((err: any) => {
				captureError(err, "Error on stopping wrapup", {
					interactionId: wrapUp?.mediaId,
				});
				showMessage("workspace", {
					intent: "error",
					text: `${intl.formatMessage({ id: "Common.Error" })} ${err}`,
				});
			});
		}
	};

	const [matchingOptions, setMatchingOptions] = useState([
		...(selectedCode.name
			? options?.filter((option) =>
					option.name
						.toLowerCase()
						.startsWith(selectedCode.name?.toLowerCase() ?? "")
				) ?? []
			: options ?? []),
	]);

	const onSearchTermChange: ComboboxProps["onChange"] = (event) => {
		const value = event.target.value.trim();
		setInputCodeValue(value);
		setMatchingOptionsByValue(value);
	};

	const onCodeInputOpenChange = useCallback(
		(_: ComboboxOpenEvents, data: ComboboxOpenChangeData) => {
			if (data.open) {
				setMatchingOptionsByValue("");
				setInputCodeValue("");
			}
		},
		[selectedCode]
	);

	const [interactionPopoverType, setInteractionPopoverType] =
		useState<InteractionPopoverType | null>(null);

	const toggleInteractionPopoverOpening = useCallback(
		(type: InteractionPopoverType) => {
			setInteractionPopoverType((oldState) =>
				oldState === type ? null : type
			);
		},
		[setInteractionPopoverType]
	);

	const handleOpenChange = (_e: OpenPopoverEvents, data: OnOpenChangeData) => {
		if (!data.open) {
			setInteractionPopoverType(null);
		}
	};

	return (
		<div className={mergeClasses(wrapUpStyle.container)}>
			<Text
				align="end"
				className={mergeClasses(
					wrapUpStyle.wrapupLabel,
					options?.length !== 0 ? wrapUpStyle.wrapupLabelDisposable : ""
				)}
			>
				{intl.formatMessage({
					id: `Interaction.WrapUp`,
				})}
			</Text>
			<Flex vAlign={"center"} className={wrapUpStyle.actionableContainer}>
				{options?.length !== 0 && (
					<Combobox
						className={wrapUpStyle.dropdown}
						placeholder={intl.formatMessage({
							id: `WrapUp.CodePlaceholder`,
						})}
						value={inputCodeValue}
						onChange={onSearchTermChange}
						onOptionSelect={onCodeSelect}
						onOpenChange={onCodeInputOpenChange}
						selectedOptions={[selectedCode.id]}
						appearance="underline"
						freeform
						inlinePopup
					>
						{matchingOptions.map((option) => (
							<Option
								style={{
									paddingLeft: `${
										wrapUp?.isLevelOneHidden
											? ((option?.level ?? 0) - 2) * 15 + 8
											: ((option?.level ?? 0) - 1) * 15 + 8
									}px`,
								}}
								key={option.id}
								text={option.name}
								value={option.id}
							>
								{option.name}
							</Option>
						))}
						{matchingOptions.length === 0 ? (
							<Option key="no-results" text="">
								{intl.formatMessage({
									id: `WrapUp.NoResults`,
								})}
							</Option>
						) : null}
					</Combobox>
				)}
				<Flex
					hAlign="center"
					vAlign="center"
					className={wrapUpStyle.timerAndButtons}
				>
					<Flex vAlign="center">{timer}</Flex>
					<Flex vAlign="flex-end">
						<NotesButton
							interactionId={currentInteraction?.id}
							callId={currentInteraction?.sessionId}
							editMode={true}
							isWrapUp={true}
							canGenerateSummary={
								canGenerateSummary === true &&
								((currentQueue?.enableCallSummary ?? false) || (currentQueue?.enableTextSummary ?? false))
							}
							enableCallSummary={
								!!currentInteraction &&
								currentInteraction.isUnify &&
								(currentQueue?.enableCallSummary ?? false)
							}
							autoInsertCallSummary={currentQueue?.autoInsertCallSummary ?? false}
							enableTextSummary={currentQueue?.enableTextSummary ?? false}
							autoInsertTextSummary={currentQueue?.autoInsertTextSummary ?? false}
							toggleInteractionPopoverOpening={toggleInteractionPopoverOpening}
							interactionPopoverType={interactionPopoverType}
							onOpenChange={handleOpenChange}
						/>
						<VoiceCallSummaryButton
							interactionId={currentInteraction?.sessionId ?? currentInteraction?.id}
							interactionStartDate={currentInteraction?.startDate}
							firstAgentAssignedAt={currentInteraction?.firstAgentAssignedAt}
							canGenerateSummary={canGenerateSummary === true}
							enableCallSummary={
								!!currentInteraction &&
								!!currentInteraction.isUnify &&
								(currentQueue?.enableCallSummary ?? false)
							}
						/>
						<SquareToggleButton
							className={wrapUpStyle.button}
							icon={<Dismiss20Regular />}
							tooltip={intl.formatMessage({ id: "Interaction.Buttons.Close" })}
							onClick={endWrapUp}
						/>
					</Flex>
				</Flex>
			</Flex>
		</div>
	);
};
